import { useSyncExternalStore } from "react";

// Support Bot > Setup. Everything here is saved on the CRM backend
// (setup-api.ts, one setup per tenant): identity, branding, allowed domains,
// and the website widget's install code (widget-integration.md).

export interface AllowedDomain {
  id: string;
  domain: string; // origin, e.g. "https://example.com"
  created_at: string;
}

export interface AgentSetup {
  setupId: string | null;
  profileImageFileId: string | null;
  brandLogoFileId: string | null;
  profileImage: string; // display URL (signed download URL or local preview)
  brandLogo: string;
  windowColor: string;
  agentName: string;
  agentRole: string;
  brandName: string;
  accentColor: string;
  purpose: string;
  introMessage: string;
  domains: AllowedDomain[];
  widgetEnabled: boolean;
  // Chat behaviour. Only sent to the backend once its setup response
  // includes these fields (chatSettingsSupported), so saving keeps working
  // before the backend change ships.
  inactivityTimeoutMinutes: number;
  chatSettingsSupported: boolean;
  // From POST .../widget-installation (not part of GET setup).
  widgetKey: string;
  scriptUrl: string;
  embedCode: string;
}

export const ACCENT_COLORS = ["#4935D4", "#2563EB", "#0F766E", "#D97706", "#DC2626", "#111827"];
export const WINDOW_COLORS = ["#F1EEFD", "#EEF3FF", "#FBF3EA", "#EAF6EE", "#F1F1F1", "#FDEEEE"];
export const AGENT_ROLES = ["CRM Agent", "Customer Support", "Sales Assistant", "Technical Support"];
export const PURPOSE_MAX = 300;
export const TIMEOUT_OPTIONS = [15, 30, 45, 60, 120];
export const INTRO_MAX = 200;

const DEFAULTS: AgentSetup = {
  setupId: null,
  profileImageFileId: null,
  brandLogoFileId: null,
  profileImage: "",
  brandLogo: "",
  windowColor: WINDOW_COLORS[0],
  agentName: "",
  agentRole: AGENT_ROLES[0],
  brandName: "",
  accentColor: ACCENT_COLORS[0],
  purpose: "",
  introMessage: "",
  domains: [],
  widgetEnabled: false,
  inactivityTimeoutMinutes: 30,
  chatSettingsSupported: false,
  widgetKey: "",
  scriptUrl: "",
  embedCode: "",
};

let state = DEFAULTS;
const listeners = new Set<() => void>();

export const getSetup = () => state;

export const updateSetup = (patch: Partial<AgentSetup>) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

export const useAgentSetup = () =>
  useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state
  );

// "Company name" in the intro message becomes the brand name.
export const resolvedIntro = (s: AgentSetup) =>
  s.introMessage.replace(/[“"]?company name[”"]?/gi, s.brandName || "your company");

// The backend stores HTTP(S) origins: "Example.com" -> "https://example.com".
// Paths, queries and wildcards aren't allowed, so those are rejected here
// rather than silently cut off.
export const toOrigin = (raw: string): string | null => {
  const text = raw.trim();
  if (!text || /\s|\*/.test(text)) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    if (!/^https?:$/.test(url.protocol) || url.username || url.password || url.search || url.hash || url.pathname !== "/") return null;
    return /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$|^localhost$/.test(url.hostname) ? url.origin : null;
  } catch {
    return null;
  }
};
